import React, {
  createContext,
  useState,
  useMemo,
  useContext,
  PropsWithChildren,
  useEffect,
} from "react";
import { usePostLogin } from "@/api/pizza-checker/auth/auth";
import { TokenResponseData, Role, RolesResponseData, JWTPayload } from "@/api/custom-schema";
import { useGetRoles } from "@/api/pizza-checker/roles/roles";
import {
  googleSignInFailure,
  passwordSignInFailure,
  postGoogleCredential,
  type GoogleSignInFailure,
  type PasswordSignInFailure,
} from "@/api/sign-in";
import { decodeJWT, isTokenExpired } from "@/lib/jwt-utils";

interface Identity {
  email: string;
  token: string;
  payload?: JWTPayload;
  userRole?: Role;
}

interface IdentityContextType {
  identity: Identity | null;
  roles: Role[] | null;
  rolesLoading: boolean;
  /** Password sign-in; null on success, else why it failed. */
  login: (login: string, password: string) => Promise<PasswordSignInFailure | null>;
  /** Sign-in with a Google ID token of the company Workspace; null on success, else why it was refused. */
  loginWithGoogle: (credential: string) => Promise<GoogleSignInFailure | null>;
  logout: () => void;
}

const IdentityContext = createContext<IdentityContextType | null>(null);

export const IdentityProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { mutateAsync: postToken } = usePostLogin();
  const [identity, setIdentity] = useState<Identity | null>(() => {
    const storedIdentity = localStorage.getItem("identity");
    if (storedIdentity) {
      const parsed = JSON.parse(storedIdentity);
      // Decode JWT payload and check if token is expired
      const payload = decodeJWT(parsed.token);
      if (payload && !isTokenExpired(payload)) {
        return {
          ...parsed,
          payload,
        };
      } else {
        // Token is expired, remove it
        localStorage.removeItem("identity");
        return null;
      }
    }
    return null;
  });

  // Fetch roles when identity is available
  const {
    data: rolesData,
    isLoading: rolesLoading,
  } = useGetRoles({
    query: {
      enabled: !!identity?.token,
    },
  });

  // Extract roles from the response and find user's role
  const roles = useMemo(() => {
    return (rolesData as RolesResponseData)?.roles || null;
  }, [rolesData]);

  // Update identity with user role when roles are loaded
  useEffect(() => {
    if (identity?.payload?.role_id && roles) {
      const userRole = roles.find(role => role._id === identity.payload?.role_id);
      if (userRole && !identity.userRole) {
        setIdentity(prev => prev ? { ...prev, userRole } : null);
      }
    }
  }, [identity?.payload?.role_id, roles, identity?.userRole]);

  const storeIdentity = (email: string, token: string) => {
    const payload = decodeJWT(token);
    if (!payload) {
      throw new Error("Invalid token received");
    }

    if (isTokenExpired(payload)) {
      throw new Error("Received expired token");
    }

    const newIdentity = {
      email,
      token,
      payload,
    };
    setIdentity(newIdentity);
    localStorage.setItem("identity", JSON.stringify(newIdentity));
  };

  const login = async (login: string, password: string) => {
    try {
      const response = (await postToken({
        data: { login, password },
      })) as TokenResponseData;

      storeIdentity(login, response.token);
      return null;
    } catch (error) {
      console.error("Login failed", error);
      return passwordSignInFailure(error);
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      const response = await postGoogleCredential(credential);
      storeIdentity(response.login, response.token);
      return null;
    } catch (error) {
      console.error("Google sign-in failed", error);
      return googleSignInFailure(error);
    }
  };

  const logout = async () => {
    setIdentity(null);
    localStorage.removeItem("identity");
  };

  return (
    <IdentityContext.Provider
      value={useMemo(() => ({ 
        identity, 
        roles, 
        rolesLoading,
        login,
        loginWithGoogle,
        logout
      }), [identity, roles, rolesLoading])}
    >
      {children}
    </IdentityContext.Provider>
  );
};

export const useIdentity = () => {
  const context = useContext(IdentityContext);
  if (!context) {
    throw new Error("useIdentity must be used within an IdentityProvider");
  }
  return context;
};
